import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Sparkles, BookOpen } from 'lucide-react';

/* /whats-new — public changelog (Aug 2026). The in-app What's New popup shows
   the latest release once; this page keeps the full history for returning
   users who want to see everything that changed since their last visit. */

const RELEASES = [
  {
    version: '9.1',
    date: 'September 2026',
    latest: true,
    items: [
      { audience: 'Admins', text: 'Invoice numbering set once — every job and Daily Site Invoice takes the next number in sequence on first download/email/QuickBooks push and keeps it forever; manual override with audit trail; Invoice Register report with month-end XLSX schedule.' },
      { audience: 'Admins', text: 'Recurring invoices — repeat a Daily Site Invoice or a completed job\u2019s invoice every day/week/2 weeks/month/quarter/year, as drafts (review queue with Issue all) or issued; live preview of the next three run dates.' },
      { audience: 'Admins', text: 'Recurring & Scheduled — one Settings list of every recurring invoice, scheduled customer report and payment-reminder rule with next run, pause/resume and last result.' },
      { audience: 'Admins', text: 'Customer statements & scheduled reports — branded statement PDF for any date range; weekly/monthly/month-end reports per customer with Summary / Standard / Detailed privacy (never worker names, badges or addresses by default).' },
      { audience: 'Admins', text: 'Customer account page — a private link where customers see their balance and open invoices and download statements and past reports themselves.' },
      { audience: 'Admins', text: 'Payment reminders — automatic emails (with statement attached) 3/14/30 days after an invoice is past due; steps, recipients and "Send now" per customer.' },
      { audience: 'Admins', text: 'Company Defaults — worker job visibility (My jobs / My team\u2019s jobs), Team chip, payroll page memory, default reports and Daily Site Invoice template.' },
      { audience: 'Admins', text: 'Dashboard money tiles now trend on real data — Estimate performance shows the full pipeline including drafts; Revenue by Project sums actual invoice totals; profitability falls back to a by-customer view.' },
      { audience: 'Admins', text: 'Payroll Workbench and Manage Users keep your place after saving — the edited row refreshes in place, stays expanded and focused.' },
      { audience: 'Workers', text: 'Jobs list shows only your own jobs (or your team\u2019s, if your company allows) — never the whole company\u2019s.' },
      { audience: 'Everyone', text: 'Manual v9.1 — new Admin Setup tab with a configuration guide per feature, and the Invoicing guide now covers recurring invoices, statements and reminders in English, Spanish and French.' },
    ],
  },
  {
    version: '9.0',
    date: 'August 2026',
    latest: false,
    items: [
      { audience: 'Supervisors', text: 'Team clock-in roster dialog — checkboxes for every member, Select all / Clear all, on-the-clock badges, and selective clock-out.' },
      { audience: 'Supervisors', text: 'Absence reasons — tag why someone was unchecked (Vacation / Sick / No-show / Other) right from the roster.' },
      { audience: 'Supervisors', text: 'Shift-only temps (mutual coverage) — punch any company worker in with your team for one shift without changing the roster.' },
      { audience: 'Supervisors', text: 'Roster pre-checked from today\u2019s shift schedule, on the dashboard and the Teams page alike.' },
      { audience: 'Admins', text: 'No-Shows & Absences report — day by day, who was scheduled but never punched in, with supervisor-tagged reasons.' },
      { audience: 'Admins', text: 'Hours by Project & Hours by Job — payroll punches vs job work-sessions side by side, variance alerts, customer filter, bar/pie charts, per-worker drill-down and scheduled email.' },
      { audience: 'Admins', text: 'Payroll pay columns — Hourly Rate, Regular Pay, OT Pay (1.5×) and Gross Pay per worker, plus an Est. Gross Pay total and CSV export.' },
      { audience: 'Admins', text: 'Temp hours flagged in payroll — purple Temp session flags and a TEMP badge per worker.' },
      { audience: 'Admins', text: 'Clock-in method restriction per team — any method, kiosk only, or Site-QR required (workers only).' },
      { audience: 'Workers', text: 'Meal-break compliance — clocking out after 5+ hours without a logged break asks whether you took a meal break; choosing Yes deducts an unpaid 30 minutes (Alberta ESA).' },
      { audience: 'Workers', text: 'Jobs can be linked to a Project when created, so project reports capture every job.' },
      { audience: 'Everyone', text: 'User manual v9.0 — dedicated Squad Leaders tab, roles-at-a-glance overview, and printable one-page quick-starts in English, Spanish and French.' },
    ],
  },
  {
    version: '8.0',
    date: 'August 2026',
    items: [
      { audience: 'Everyone', text: 'Time Off & PTO — request vacation or sick time, supervisor approvals, balances and a company PTO calendar.' },
      { audience: 'Everyone', text: 'Offline clock-in — punches save on the device without signal and sync automatically with the original time.' },
      { audience: 'Supervisors', text: 'Weekly schedule grid with drag-and-drop shifts, Copy Last Week, and time-off conflict warnings.' },
      { audience: 'Supervisors', text: 'Bulk team adds and CSV import to build whole teams in seconds.' },
      { audience: 'Admins', text: 'Payroll Workbench pre-lock checklist and pay-period locking.' },
      { audience: 'Admins', text: 'Job Margins report with labor/material drill-downs and budget overrun alerts.' },
      { audience: 'Everyone', text: 'Ask HinesTime AI — the floating assistant answers questions from your live company data.' },
    ],
  },
  {
    version: '7.x and earlier',
    date: '2026',
    items: [
      { audience: 'Everyone', text: 'Kiosk clock stations with PINs, punch photos and offline queueing; Site QR posters for on-site verification.' },
      { audience: 'Supervisors', text: 'Squads with crowned squad leaders who punch their own crew in and out; supervisor approvals queue.' },
      { audience: 'Admins', text: 'QuickBooks time & invoice sync, estimates, job labels with billing multipliers, audit log, 30+ reports with CSV / PDF / scheduled email.' },
      { audience: 'Everyone', text: 'Multi-language interface (English, Spanish, French), dark mode, and the in-app user manual.' },
    ],
  },
];

const AUDIENCE_COLORS = {
  Everyone: { color: '#00C896', bg: 'rgba(0,200,150,0.12)' },
  Workers: { color: '#1E88E5', bg: 'rgba(30,136,229,0.12)' },
  Supervisors: { color: '#A78BFA', bg: 'rgba(167,139,250,0.15)' },
  Admins: { color: '#FFA000', bg: 'rgba(255,160,0,0.12)' },
};

export default function Changelog() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen pb-20" style={{ background: '#0B1321' }} data-testid="changelog-page">
      <div className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <Button onClick={() => navigate(-1)} variant="ghost" size="sm" className="text-white/80 hover:text-white mb-3 -ml-2" data-testid="changelog-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8" />
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">What&apos;s New in HinesTime</h1>
          </div>
          <p className="text-blue-50 mt-1 text-sm">Every release, in one place — so you can catch up on anything you missed since your last visit.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {RELEASES.map((rel) => (
          <div key={rel.version} className="rounded-2xl border p-6" style={{ background: '#0F1923', borderColor: rel.latest ? 'rgba(30,136,229,0.5)' : '#2D3748' }} data-testid={`changelog-v${rel.version.split(' ')[0]}`}>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <h2 className="text-xl font-bold text-white">Version {rel.version}</h2>
              <span className="text-xs" style={{ color: '#64748B' }}>{rel.date}</span>
              {rel.latest && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide" style={{ color: '#1E88E5', background: 'rgba(30,136,229,0.15)' }}>Latest</span>
              )}
            </div>
            <ul className="space-y-2.5">
              {rel.items.map((it, i) => {
                const c = AUDIENCE_COLORS[it.audience] || AUDIENCE_COLORS.Everyone;
                return (
                  <li key={i} className="flex gap-3 items-start text-sm leading-relaxed" style={{ color: '#CBD5E1' }}>
                    <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold mt-0.5 w-24 text-center" style={{ color: c.color, background: c.bg }}>{it.audience}</span>
                    <span>{it.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="text-center">
          <Button onClick={() => navigate('/user-manual')} className="text-white" style={{ background: '#1E88E5' }} data-testid="changelog-manual-btn">
            <BookOpen className="w-4 h-4 mr-2" /> Read the full manual
          </Button>
        </div>
      </div>
    </div>
  );
}
